/**
 * Configuration fails at boot, loudly, rather than at the first request that
 * needs it (LLD §3.6, NFR-DSH-17). The variables are PDD Appendix B's.
 *
 * Hand-rolled rather than schema-validated on purpose (carried over from
 * akav-eli): this module is in the SHELL graph, loaded on every visit, and
 * pulling a validation library in for a handful of variables cost ~14 KB
 * gzipped. Zod is still the tool for forms, where it lands in feature chunks.
 * The behaviour is pinned by env.test.ts either way. In the container image,
 * `window.__ENV__` overrides the build's values (`envSource`, Task 7.3).
 */

export interface Env {
  /** The v2 base: `/api/v2` (same-origin) or an absolute URL ending in `/api/v2`. */
  VITE_API_BASE_URL: string
  /** The public API origin shown in the API guide (v1 snippets). */
  VITE_PUBLIC_API_URL: string
  VITE_ENABLE_MOCKS: "true" | "false"
  VITE_DEFAULT_LOCALE: "fr" | "en"
  VITE_TURNSTILE_SITE_KEY?: string
  VITE_GOOGLE_CLIENT_ID?: string
  VITE_ANDROID_APK_URL?: string
  VITE_APP_VERSION: string
  VITE_COMMIT_SHA: string
  readonly enableMocks: boolean
}

interface Issue {
  key: string
  message: string
}

const isUrl = (value: string): boolean => {
  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

function requiredUrl(raw: Record<string, unknown>, key: string, issues: Issue[]): string {
  const value = raw[key]
  if (typeof value !== "string" || value.trim() === "") {
    issues.push({ key, message: "is required" })
    return ""
  }
  if (!isUrl(value)) {
    issues.push({ key, message: `must be a valid URL, got "${value}"` })
    return ""
  }
  return value
}

/** An absolute URL, or a same-origin path (the Vite proxy and nginx serve `/api`). */
function requiredBase(raw: Record<string, unknown>, key: string, issues: Issue[]): string {
  const value = raw[key]
  if (typeof value === "string" && value.startsWith("/") && !value.startsWith("//")) return value
  return requiredUrl(raw, key, issues)
}

function optionalString(raw: Record<string, unknown>, key: string): string | undefined {
  const value = raw[key]
  return typeof value === "string" && value !== "" ? value : undefined
}

function optionalUrl(
  raw: Record<string, unknown>,
  key: string,
  issues: Issue[],
): string | undefined {
  const value = raw[key]
  if (value === undefined || value === "") return undefined
  if (typeof value !== "string" || !isUrl(value)) {
    issues.push({ key, message: `must be a valid URL when set, got "${typeof value}"` })
    return undefined
  }
  return value
}

function enumOr<T extends string>(
  raw: Record<string, unknown>,
  key: string,
  allowed: readonly T[],
  fallback: T,
  issues: Issue[],
): T {
  const value = raw[key]
  if (value === undefined || value === "") return fallback
  if (typeof value !== "string" || !allowed.includes(value as T)) {
    issues.push({
      key,
      message: `must be one of ${allowed.join(" | ")}, got "${typeof value === "string" ? value : typeof value}"`,
    })
    return fallback
  }
  return value as T
}

function stringOr(raw: Record<string, unknown>, key: string, fallback: string): string {
  const value = raw[key]
  return typeof value === "string" && value !== "" ? value : fallback
}

/**
 * Pure so it can be tested without reaching for import.meta.env. Reports EVERY
 * invalid variable in one throw -- fixing configuration one error per restart
 * is its own small hell.
 */
export function parseEnv(raw: unknown): Env {
  const source = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>
  const issues: Issue[] = []

  const apiBaseUrl = requiredBase(source, "VITE_API_BASE_URL", issues)
  const publicApiUrl = requiredUrl(source, "VITE_PUBLIC_API_URL", issues)
  const enableMocks = enumOr(
    source,
    "VITE_ENABLE_MOCKS",
    ["true", "false"] as const,
    "false",
    issues,
  )
  const locale = enumOr(source, "VITE_DEFAULT_LOCALE", ["fr", "en"] as const, "fr", issues)
  const apkUrl = optionalUrl(source, "VITE_ANDROID_APK_URL", issues)
  const turnstileSiteKey = optionalString(source, "VITE_TURNSTILE_SITE_KEY")
  const googleClientId = optionalString(source, "VITE_GOOGLE_CLIENT_ID")

  if (issues.length > 0) {
    throw new Error(
      "Invalid environment configuration:\n" +
        issues.map((i) => `  ${i.key}: ${i.message}`).join("\n"),
    )
  }

  return {
    VITE_API_BASE_URL: apiBaseUrl,
    VITE_PUBLIC_API_URL: publicApiUrl,
    VITE_ENABLE_MOCKS: enableMocks,
    VITE_DEFAULT_LOCALE: locale,
    ...(turnstileSiteKey !== undefined && { VITE_TURNSTILE_SITE_KEY: turnstileSiteKey }),
    ...(googleClientId !== undefined && { VITE_GOOGLE_CLIENT_ID: googleClientId }),
    ...(apkUrl !== undefined && { VITE_ANDROID_APK_URL: apkUrl }),
    VITE_APP_VERSION: stringOr(source, "VITE_APP_VERSION", "dev"),
    VITE_COMMIT_SHA: stringOr(source, "VITE_COMMIT_SHA", "local"),
    enableMocks: enableMocks === "true",
  }
}

/**
 * What the app runs with (Task 7.3): what the build baked in, overridden by
 * the container's `/env.js` (`window.__ENV__`, written at start from the
 * `DASHBOARD_*` variables), so one image serves every environment. An empty
 * value keeps the build's; mocks stay a build-time choice.
 */
export function envSource(
  build: Record<string, unknown>,
  runtime: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const out = { ...build }
  for (const [key, value] of Object.entries(runtime ?? {})) {
    if (key !== "VITE_ENABLE_MOCKS" && typeof value === "string" && value !== "") out[key] = value
  }
  return out
}

declare global {
  interface Window {
    __ENV__?: Record<string, unknown>
  }
}

export const env: Env = parseEnv(
  envSource(import.meta.env, typeof window === "undefined" ? undefined : window.__ENV__),
)
